import { useEffect, useMemo, useState } from "react";
import client from "../api/client";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { useFinancialYear } from "../context/FinancialYearContext";
import { useAuth } from "../context/AuthContext";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const styles = {
  page: { fontSize: 11, minHeight: "100%", overflow: "visible", padding: 0 },
  card: {
    border: 0,
    borderRadius: 14,
    overflow: "hidden",
    boxShadow: "0 2px 10px rgba(0,0,0,.08)",
    background: "#fff",
  },
  header: {
    background: "linear-gradient(90deg, #22052D, #CCB3D1)",
    color: "#fff",
    padding: "8px 12px",
  },
  title: { fontSize: 13, fontWeight: 700, margin: 0 },
  sub: { fontSize: 10, opacity: 0.85 },
  body: { padding: 10 },
  label: { fontSize: 10, fontWeight: 700, marginBottom: 3, color: "#495057" },
  input: { height: 30, fontSize: 11, borderRadius: 7 },
  btn: { height: 30, fontSize: 11, borderRadius: 7, fontWeight: 600 },
  box: {
    borderRadius: 12,
    background: "#f8f9fa",
    border: "1px solid #e9ecef",
    padding: 10,
    height: "100%",
  },
  statLabel: { fontSize: 10, fontWeight: 700 },
  statValue: { fontSize: 15, fontWeight: 800, marginBottom: 7 },
  tableWrap: {
    overflowY: "auto",
    overflowX: "auto",
    border: "1px solid #e9ecef",
    borderRadius: 10,
  },
  table: { fontSize: 11, minWidth: 450, marginBottom: 0 },
  th: {
    fontSize: 10,
    padding: "7px 8px",
    whiteSpace: "nowrap",
    background: "#f8f9fa",
    position: "sticky",
    top: 0,
    zIndex: 1,
  },
  td: {
    fontSize: 11,
    padding: "6px 8px",
    verticalAlign: "middle",
    whiteSpace: "nowrap",
  },
};

export default function BalanceSheetPage() {
  const { financialYear } = useFinancialYear();
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [fromDate, setFromDate] = useState(financialYear.fromDate);
  const [toDate, setToDate] = useState(financialYear.toDate);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setFromDate(financialYear.fromDate);
    setToDate(financialYear.toDate);
  }, [financialYear.fromDate, financialYear.toDate]);

  const run = async () => {
    try {
      setLoading(true);
      const q = new URLSearchParams({ fromDate, toDate });
      if (user?.companyId) q.set("companyId", user.companyId);
      const res = await client.get(`/finance/balance-sheet?${q.toString()}`);
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch {
      alert("Failed to load balance sheet");
    } finally {
      setLoading(false);
    }
  };

  const assets = useMemo(
    () => rows.filter((r) => r.AccountType === "Asset"),
    [rows],
  );
  const liabilities = useMemo(
    () => rows.filter((r) => r.AccountType === "Liability"),
    [rows],
  );
  const equity = useMemo(
    () => rows.filter((r) => r.AccountType === "Equity"),
    [rows],
  );

  const totalAssets = assets.reduce((sum, r) => sum + Number(r.Amount || 0), 0);
  const totalLiabilities = liabilities.reduce(
    (sum, r) => sum + Number(r.Amount || 0),
    0,
  );
  const totalEquity = equity.reduce((sum, r) => sum + Number(r.Amount || 0), 0);
  const totalLiabilitiesAndEquity = totalLiabilities + totalEquity;
  const difference = totalAssets - totalLiabilitiesAndEquity;
  const isBalanced = Math.abs(difference) < 0.01;

  const exportToExcel = () => {
    if (!rows.length) return alert("No data to export");

    const data = rows.map((r) => ({
      Type: r.AccountType,
      Account: r.AccountName,
      Amount: Number(r.Amount || 0).toFixed(2),
    }));

    data.push(
      {},
      {
        Type: "Asset",
        Account: "Total Assets",
        Amount: totalAssets.toFixed(2),
      },
      {
        Type: "Liability",
        Account: "Total Liabilities",
        Amount: totalLiabilities.toFixed(2),
      },
      {
        Type: "Equity",
        Account: "Total Equity",
        Amount: totalEquity.toFixed(2),
      },
      {
        Type: "",
        Account: "Total Liabilities + Equity",
        Amount: totalLiabilitiesAndEquity.toFixed(2),
      },
    );

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "BalanceSheet");
    XLSX.writeFile(workbook, "BalanceSheet.xlsx");
  };

  const exportToPDF = () => {
    if (!rows.length) return alert("No data to export");

    const doc = new jsPDF();
    doc.setFontSize(14);
    doc.text("Balance Sheet", 14, 15);
    doc.setFontSize(9);
    doc.text(`From: ${fromDate || "-"}  To: ${toDate || "-"}`, 14, 23);

    autoTable(doc, {
      startY: 28,
      head: [["Type", "Account", "Amount"]],
      body: rows.map((r) => [
        r.AccountType,
        r.AccountName,
        Number(r.Amount || 0).toFixed(2),
      ]),
      styles: { fontSize: 8 },
      headStyles: { fontSize: 8 },
    });

    const finalY = doc.lastAutoTable.finalY + 8;
    doc.setFontSize(9);
    doc.text(`Total Assets: ${totalAssets.toFixed(2)}`, 14, finalY);
    doc.text(
      `Total Liabilities: ${totalLiabilities.toFixed(2)}`,
      14,
      finalY + 7,
    );
    doc.text(`Total Equity: ${totalEquity.toFixed(2)}`, 14, finalY + 14);
    doc.text(
      `Total Liabilities + Equity: ${totalLiabilitiesAndEquity.toFixed(2)}`,
      14,
      finalY + 21,
    );

    doc.save("BalanceSheet.pdf");
  };

  const chartData = {
    labels: ["Assets", "Liabilities", "Equity"],
    datasets: [
      { label: "Amount", data: [totalAssets, totalLiabilities, totalEquity] },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { labels: { font: { size: 10 } } },
      tooltip: { bodyFont: { size: 10 }, titleFont: { size: 10 } },
    },
    scales: {
      x: { ticks: { font: { size: 10 } } },
      y: { ticks: { font: { size: 10 } } },
    },
  };

  return (
    <div className="container-fluid py-2" style={styles.page}>
      <div style={styles.card}>
        <div style={styles.header}>
          <h6 style={styles.title}>
            <i className="bi bi-file-earmark-bar-graph me-2"></i>
            Balance Sheet
          </h6>
          <div style={styles.sub}>
            Assets, liabilities and equity position as on selected date
          </div>
        </div>

        <div style={styles.body}>
          <div className="row g-2 mb-2 align-items-end">
            <Field label="From Date" className="col-md-2">
              <input
                type="date"
                className="form-control form-control-sm"
                style={styles.input}
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </Field>
            <Field label="As On Date" className="col-md-2">
              <input
                type="date"
                className="form-control form-control-sm"
                style={styles.input}
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            </Field>

            <div className="col-md-2 d-grid">
              <button
                className="btn btn-success btn-sm"
                style={styles.btn}
                onClick={run}
                disabled={loading}
              >
                <i className="bi bi-search me-1"></i>
                {loading ? "Loading..." : "Load"}
              </button>
            </div>

            <div className="col-md-2 d-grid">
              <button
                className="btn btn-outline-primary btn-sm"
                style={styles.btn}
                onClick={exportToExcel}
                disabled={!rows.length}
              >
                <i className="bi bi-file-earmark-excel me-1"></i>
                Excel
              </button>
            </div>

            <div className="col-md-2 d-grid">
              <button
                className="btn btn-outline-danger btn-sm"
                style={styles.btn}
                onClick={exportToPDF}
                disabled={!rows.length}
              >
                <i className="bi bi-file-earmark-pdf me-1"></i>
                PDF
              </button>
            </div>

            <div className="col-md-3 d-flex align-items-end">
              <div
                className={`w-100 text-center ${isBalanced ? "text-success" : "text-danger"}`}
                style={{
                  height: 30,
                  borderRadius: 7,
                  background: isBalanced ? "#d1e7dd" : "#f8d7da",
                  fontSize: 11,
                  fontWeight: 700,
                  paddingTop: 6,
                }}
              >
                {isBalanced
                  ? "Balanced"
                  : `Difference ₹ ${Math.abs(difference).toFixed(2)}`}
              </div>
            </div>
          </div>

          <div className="row g-2 mb-2">
            <div className="col-md-8">
              <div style={styles.box}>
                <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>
                  Balance Sheet Chart
                </div>
                <div style={{ height: 215 }}>
                  <Bar data={chartData} options={chartOptions} />
                </div>
              </div>
            </div>

            <div className="col-md-4">
              <div style={styles.box}>
                <Stat label="Assets" value={totalAssets} color="#198754" />
                <Stat
                  label="Liabilities"
                  value={totalLiabilities}
                  color="#dc3545"
                />
                <Stat label="Equity" value={totalEquity} color="#0d6efd" />
                <Stat
                  label="Liabilities + Equity"
                  value={totalLiabilitiesAndEquity}
                  color="#212529"
                />

                <span
                  className={`badge ${isBalanced ? "text-bg-success" : "text-bg-danger"}`}
                  style={{ fontSize: 10 }}
                >
                  {isBalanced ? "Balanced" : "Not Balanced"}
                </span>
              </div>
            </div>
          </div>

          <div className="row g-2">
            <ReportTable
              title="Assets"
              titleColor="#198754"
              rows={assets}
              totalLabel="Total Assets"
              total={totalAssets}
              maxHeight={260}
            />
            <div className="col-md-6">
              <ReportTableInner
                title="Liabilities"
                titleColor="#dc3545"
                rows={liabilities}
                totalLabel="Total Liabilities"
                total={totalLiabilities}
                maxHeight={180}
              />
              <ReportTableInner
                title="Equity"
                titleColor="#0d6efd"
                rows={equity}
                totalLabel="Total Equity"
                total={totalEquity}
                maxHeight={180}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, className, children }) {
  return (
    <div className={className}>
      <label style={styles.label}>{label}</label>
      {children}
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <>
      <div style={{ ...styles.statLabel, color }}>{label}</div>
      <div style={styles.statValue}>₹ {value.toFixed(2)}</div>
    </>
  );
}

function ReportTable({
  title,
  titleColor,
  rows,
  totalLabel,
  total,
  maxHeight,
}) {
  return (
    <div className="col-md-6">
      <ReportTableInner
        title={title}
        titleColor={titleColor}
        rows={rows}
        totalLabel={totalLabel}
        total={total}
        maxHeight={maxHeight}
      />
    </div>
  );
}

function ReportTableInner({
  title,
  titleColor,
  rows,
  totalLabel,
  total,
  maxHeight,
}) {
  return (
    <>
      <h6
        style={{
          fontSize: 12,
          fontWeight: 700,
          color: titleColor,
          marginBottom: 5,
        }}
      >
        {title}
      </h6>

      <div style={{ ...styles.tableWrap, maxHeight, marginBottom: 8 }}>
        <table
          className="table table-bordered table-hover align-middle"
          style={styles.table}
        >
          <thead>
            <tr>
              <th style={{ ...styles.th, minWidth: 250 }}>Account</th>
              <th className="text-end" style={{ ...styles.th, minWidth: 120 }}>
                Amount
              </th>
            </tr>
          </thead>

          <tbody>
            {!rows.length ? (
              <tr>
                <td
                  colSpan="2"
                  className="text-center text-muted"
                  style={{ ...styles.td, padding: 18 }}
                >
                  No records
                </td>
              </tr>
            ) : (
              rows.map((r, i) => (
                <tr key={i}>
                  <td style={styles.td}>{r.AccountName}</td>
                  <td className="text-end" style={styles.td}>
                    {Number(r.Amount || 0).toFixed(2)}
                  </td>
                </tr>
              ))
            )}
          </tbody>

          <tfoot>
            <tr>
              <td
                style={{ ...styles.td, fontWeight: 700, background: "#f8f9fa" }}
              >
                {totalLabel}
              </td>
              <td
                className="text-end"
                style={{ ...styles.td, fontWeight: 700, color: titleColor }}
              >
                {total.toFixed(2)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </>
  );
}
